<template>
  <div class="dietary-form-fields">
    <!-- 1. How they usually eat -->
    <section class="form-section">
      <p class="form-question">{{ labels.howEat }}</p>
      <div class="pill-group">
        <button
          v-for="option in dietaryOptions"
          :key="option.key"
          type="button"
          class="iabf-chip"
          :class="{ 'is-selected': value.diet === option.key }"
          @click="selectDiet(option.key)"
        >
          {{ option.short }}
        </button>
      </div>
      <p v-if="value.diet" class="field-hint diet-detail">{{ dietDetail }}</p>
    </section>

    <!-- 2. Foods within that style -->
    <section v-if="dietDetailOptions.length" class="form-section">
      <p class="form-question">{{ labels.happyWith }}</p>
      <p class="field-hint">Just a quick yes or no for each.</p>
      <ul class="detail-answer-list">
        <li
          v-for="opt in dietDetailOptions"
          :key="opt"
          class="detail-answer-row"
        >
          <span class="detail-answer-label">{{ opt }}</span>
          <div class="pill-group">
            <button
              type="button"
              class="iabf-chip"
              :class="{ 'is-selected': value.dietDetailAnswers[opt] === true }"
              @click="setDietDetailAnswer(opt, true)"
            >
              Yes
            </button>
            <button
              type="button"
              class="iabf-chip"
              :class="{ 'is-selected': value.dietDetailAnswers[opt] === false }"
              @click="setDietDetailAnswer(opt, false)"
            >
              No
            </button>
          </div>
        </li>
      </ul>
    </section>

    <!-- Gluten -->
    <section class="form-section">
      <p class="form-question">{{ qGluten }}. {{ labels.gluten }}</p>
      <p class="field-hint">Let us know so we can plan ahead for kitchen safety.</p>
      <div class="pill-group">
        <button
          v-for="opt in glutenOptions"
          :key="opt.key"
          type="button"
          class="iabf-chip"
          :class="{ 'is-selected': value.gluten === opt.key }"
          @click="patch({ gluten: opt.key })"
        >
          {{ opt.label }}
        </button>
      </div>
    </section>

    <!-- Allergies -->
    <section class="form-section">
      <p class="form-question">{{ qAllergens }}. {{ labels.allergens }}</p>
      <p class="field-hint">Tap everything that applies — or None if they’re all set.</p>
      <div class="pill-group">
        <button
          v-for="opt in foodAllergiesOptions"
          :key="opt"
          type="button"
          class="iabf-chip"
          :class="{ 'is-selected': (value.foodAllergies || []).includes(opt) }"
          @click="toggleAllergy(opt)"
        >
          {{ opt }}
        </button>
      </div>
    </section>

    <!-- Notes -->
    <section class="form-section">
      <p class="form-question">
        {{ qNotes }}. {{ labels.notes }}
        <span class="optional-tag">optional</span>
      </p>
      <input
        class="iabf-input notes-input"
        type="text"
        :value="value.dietaryNotes"
        :placeholder="labels.notesPlaceholder"
        @input="patch({ dietaryNotes: $event.target.value })"
      >
    </section>
  </div>
</template>

<script>
import {
  DIETARY_OPTIONS,
  DETAIL_BY_DIET,
  FOOD_ALLERGIES_OPTIONS,
  GLUTEN_OPTIONS,
  dietDetailOptionsFor,
  emptyDietaryFormState
} from '@/config/iabf-dietary'

export default {
  name: 'DietaryFormFields',
  props: {
    /** Form state: diet, dietDetailAnswers, dietDetails, gluten, foodAllergies, dietaryNotes */
    value: {
      type: Object,
      default: () => emptyDietaryFormState()
    },
    /**
     * 'self' = dancer form copy (“you”)
     * 'guest' = guest registration (“your guest”)
     */
    subject: {
      type: String,
      default: 'self'
    }
  },
  data () {
    return {
      dietaryOptions: DIETARY_OPTIONS,
      glutenOptions: GLUTEN_OPTIONS,
      foodAllergiesOptions: FOOD_ALLERGIES_OPTIONS
    }
  },
  computed: {
    isGuest () {
      return this.subject === 'guest'
    },
    labels () {
      if (this.isGuest) {
        return {
          howEat: '1. How does your guest usually eat?',
          happyWith: '2. Happy with these on their plate?',
          gluten: 'Is gluten okay for them?',
          allergens: 'Anything else we should watch for?',
          notes: 'Anything else?',
          notesPlaceholder: 'Likes, dislikes, or anything we might have missed…'
        }
      }
      return {
        howEat: '1. How do you usually eat?',
        happyWith: '2. Happy with these on your plate?',
        gluten: 'Is gluten okay for you?',
        allergens: 'Anything else we should watch for?',
        notes: 'Anything else?',
        notesPlaceholder: 'Likes, dislikes, or anything we might have missed…'
      }
    },
    dietDetail () {
      const match = DIETARY_OPTIONS.find(o => o.key === this.value.diet)
      return match ? match.description : ''
    },
    dietDetailOptions () {
      return dietDetailOptionsFor(this.value.diet)
    },
    qGluten () {
      return this.dietDetailOptions.length ? 3 : 2
    },
    qAllergens () {
      return this.dietDetailOptions.length ? 4 : 3
    },
    qNotes () {
      return this.dietDetailOptions.length ? 5 : 4
    }
  },
  methods: {
    patch (partial) {
      this.$emit('input', Object.assign({}, this.value, partial))
    },
    selectDiet (key) {
      this.patch({
        diet: key,
        dietDetailAnswers: {},
        dietDetails: []
      })
    },
    setDietDetailAnswer (opt, yes) {
      const answers = Object.assign({}, this.value.dietDetailAnswers || {}, { [opt]: yes })
      const options = DETAIL_BY_DIET[this.value.diet] || []
      const dietDetails = options.filter(o => answers[o] === true)
      this.patch({ dietDetailAnswers: answers, dietDetails })
    },
    toggleAllergy (option) {
      const current = Array.isArray(this.value.foodAllergies) ? this.value.foodAllergies : []
      let next
      if (option === 'None') {
        next = current.includes('None') ? [] : ['None']
      } else {
        next = current.filter(x => x !== 'None')
        if (next.includes(option)) {
          next = next.filter(x => x !== option)
        } else {
          next = next.concat([option])
        }
      }
      this.patch({ foodAllergies: next })
    }
  }
}
</script>

<style scoped>
.form-section {
  margin-bottom: 28px;
}
.form-question {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-weight: 700;
  font-size: 17px;
  letter-spacing: -0.02em;
  line-height: 1.3;
  color: var(--iabf-ink, #1c1b1f);
  margin: 0 0 10px;
}
.field-hint {
  font-size: 14px;
  color: var(--iabf-ink-muted, #5c5a63);
  margin: 0 0 12px;
  line-height: 1.45;
}
.diet-detail {
  margin-top: 10px;
  margin-bottom: 0;
}
.optional-tag {
  font-weight: 500;
  font-size: 13px;
  color: var(--iabf-ink-faint, #8a8792);
  font-family: var(--iabf-font-body, inherit);
}
.pill-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.iabf-chip {
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  background: #fff;
  border-radius: 999px;
  padding: 10px 14px;
  font-size: 14px;
  font-weight: 600;
  color: var(--iabf-ink-muted, #5c5a63);
  cursor: pointer;
  margin: 0;
  line-height: 1.3;
  font-family: var(--iabf-font-body, inherit);
}
.iabf-chip.is-selected {
  border-color: var(--iabf-primary, #8a957d);
  background: rgba(138, 149, 125, 0.12);
  color: var(--iabf-ink, #1c1b1f);
}
.detail-answer-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.detail-answer-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid var(--iabf-border, #e3e0e8);
}
.detail-answer-row:last-child {
  border-bottom: none;
}
.detail-answer-label {
  font-size: 15px;
  font-weight: 600;
  color: var(--iabf-ink, #1c1b1f);
}
.iabf-input {
  display: block;
  width: 100%;
  box-sizing: border-box;
  min-height: 48px;
  padding: 12px 14px;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 12px;
  font-family: var(--iabf-font-body, inherit);
  font-size: 16px;
  color: var(--iabf-ink, #1c1b1f);
  background: #fff;
  outline: none;
}
.iabf-input:focus {
  border-color: var(--iabf-primary, #8a957d);
  box-shadow: 0 0 0 3px var(--iabf-focus-ring, rgba(138, 149, 125, 0.28));
}
.notes-input {
  margin-top: 4px;
}
</style>
